import type { TagItem } from "@vaulted/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, post, put } from "../../api";
import { Alert, Button, Checkbox, Dialog, Field, Loading, Panel } from "../../components/ui";
import { useTags } from "../Clients";

const COLORS = ["#1E7F5C", "#B4232F", "#9A5B00", "#4A5568", "#C51A6A", "#7A4FB5", "#2B6CB0"];

export function TagsTab() {
  const tags = useTags();
  const [editing, setEditing] = useState<TagItem | "new" | null>(null);
  if (tags.isLoading) return <Loading />;
  if (tags.error || !tags.data) return <Alert>{errorMessage(tags.error)}</Alert>;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-soft">Labels for client records, like Qualiphy results. Staff add them on each client's profile.</p>
      <Panel>
        <ul className="divide-y divide-mist">
          {tags.data.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-3 px-5 py-3">
              <span className="flex items-center gap-3">
                <span className="rounded-full px-2.5 py-0.5 text-sm font-semibold text-white" style={{ background: t.color }}>
                  {t.name}
                </span>
                <span className="text-sm text-muted">
                  {t.clientCount} client{t.clientCount === 1 ? "" : "s"}
                  {t.countsAsGfe && " · counts as a good faith exam"}
                  {t.mdOnly && " · medical directors only"}
                </span>
              </span>
              <Button variant="quiet" onClick={() => setEditing(t)}>
                Edit
              </Button>
            </li>
          ))}
          {tags.data.length === 0 && <li className="px-5 py-3 text-sm text-muted">No tags yet.</li>}
        </ul>
      </Panel>
      <div>
        <Button onClick={() => setEditing("new")}>Add tag</Button>
      </div>
      {editing && <TagDialog tag={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function TagDialog({ tag, onClose }: { tag: TagItem | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState({ name: tag?.name ?? "", color: tag?.color ?? COLORS[4]!, countsAsGfe: tag?.countsAsGfe ?? false, mdOnly: tag?.mdOnly ?? false });
  const save = useMutation({
    mutationFn: () => (tag ? put<TagItem[]>(`/api/tags/${tag.id}`, v) : post<TagItem[]>("/api/tags", v)),
    onSuccess: (d) => {
      qc.setQueryData(["tags"], d);
      onClose();
    },
  });
  return (
    <Dialog
      title={tag ? "Edit tag" : "Add tag"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="tag-form" busy={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="tag-form" className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), save.mutate())}>
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Name" required autoFocus maxLength={40} value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
        <div className="flex flex-wrap gap-2">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setV({ ...v, color: c })}
              className="size-8 rounded-full ring-offset-2 aria-pressed:ring-2 aria-pressed:ring-ink"
              style={{ background: c }}
              aria-pressed={v.color === c}
              aria-label={`Color ${c}`}
            />
          ))}
        </div>
        <Checkbox
          label="Counts as a good faith exam"
          description="Adding it to a client (e.g. MD Approved) covers them for the clinic's approval period, from the day it's added."
          checked={v.countsAsGfe}
          onChange={(on) => setV({ ...v, countsAsGfe: on, mdOnly: on || v.mdOnly })}
        />
        <Checkbox label="Only medical directors can add or remove it" checked={v.mdOnly} onChange={(on) => setV({ ...v, mdOnly: on })} />
      </form>
    </Dialog>
  );
}
